import _ from 'lodash'
import { cloneNested } from '@/helpers/helpers'

const createPatientClientUid = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return `patient-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

const patientTransportStatuses = {
  pending: 'pending',
  onBoard: 'on_board',
  notTaken: 'not_taken',
  delivered: 'delivered',
  removedAfterConfirmation: 'removed_after_confirmation',
}

const patientInfoTemplate = {
  id: null,
  surname: null,
  name: null,
  age: null,
  sex: 'M',
  weight: null,
  pesel: null,
  notes: null,
  patientOnBoard: false,
  patientTransportStatus: patientTransportStatuses.pending,
  // Multi-transport addresses
  address: null,
  addressDetails: null,
  patientData: {
    noPesel: false,
    requiresCaregiver: false,
    caregiver: {
      name: null,
      surname: null,
      pesel: null,
      phone: null,
    },
    transportStatus: 'patientRemained',
    valuables: {
      identityDocument: null,
      other: false,
      otherItems: null,
    },
  },
  death: {
    time: null,
    deathOccurred: 'notOccurred',
    deathCertificateIssued: false,
  },
  summary: {
    medicalDocumentationIssued: 'notReceived',
    disinfection: false,
  },
  providedHealthServices: {
    services: [],
    activities: [],
    otherActivities: null,
  },
  diagnosis: {
    diagnosis: [],
    description: null,
  },
  healthCondition: {
    patientState: null,
    awareness: null,
    psychomotorAssessment: null,
    pupils: {
      reactionToLight: {
        left: '',
        right: '',
      },
      width: {
        left: '',
        right: '',
      },
    },
    intoxication: [],
    other: [],
    ekg: {
      list: [],
      other: null,
    },
    paresis: {
      upperLimb: {
        left: false,
        right: false,
      },
      lowerLimb: {
        left: false,
        right: false,
      },
    },
    pulse: {
      value: '',
      regular: true,
    },
    glasgowComaScale: {
      eyeResponse: 0,
      verbalResponse: 0,
      motorResponse: 0,
    },
    respiratorySystem: {
      respiratoryFailure: [],
      left: [],
      right: [],
    },
  },
}

const createEmptyPatientInfo = () => ({
  ...cloneNested(patientInfoTemplate),
  clientUid: createPatientClientUid(),
})

const mergePatientWithTemplate = patient => _.mergeWith(
  createEmptyPatientInfo(),
  patient || {},
  (objValue, srcValue) => {
    if (srcValue === null && typeof objValue === 'object') {
      return objValue
    }
    return undefined
  },
)

const resolvePatientTransportStatus = patient => {
  const status = patient?.patientTransportStatus
  const allowedStatuses = Object.values(patientTransportStatuses)
  if (allowedStatuses.includes(status)) {
    return status
  }
  return patient?.patientOnBoard
    ? patientTransportStatuses.onBoard
    : patientTransportStatuses.pending
}

const isPatientVisibleInView = patient => resolvePatientTransportStatus(patient) !== patientTransportStatuses.removedAfterConfirmation

const normalizePatientTransportState = patient => {
  const status = resolvePatientTransportStatus(patient)
  const patientOnBoard = status === patientTransportStatuses.onBoard

  return {
    ...patient,
    patientTransportStatus: status,
    patientOnBoard,
  }
}

const normalizePatientList = patients => {
  if (!patients) {
    return []
  }

  const items = Array.isArray(patients) ? patients : [patients]
  return items
    .map(item => mergePatientWithTemplate(item))
    .map(item => normalizePatientTransportState(item))
}

const hasPatientOnBoard = patients => Array.isArray(patients)
  && patients.some(patient => (
    isPatientVisibleInView(patient)
    && resolvePatientTransportStatus(patient) === patientTransportStatuses.onBoard
  ))

const getPatientCaregiver = patient => {
  const caregiver = patient?.patientData?.caregiver

  if (!caregiver || typeof caregiver !== 'object') {
    return {}
  }

  return caregiver
}

const shouldDisplayPatientCaregiver = patient => {
  if (!patient?.patientData?.requiresCaregiver) {
    return false
  }

  const caregiver = getPatientCaregiver(patient)

  return ['name', 'surname', 'pesel', 'phone'].some(field => {
    const value = caregiver?.[field]
    return typeof value === 'string' ? value.trim() !== '' : Boolean(value)
  })
}

export {
  patientTransportStatuses,
  patientInfoTemplate,
  createEmptyPatientInfo,
  createPatientClientUid,
  mergePatientWithTemplate,
  resolvePatientTransportStatus,
  isPatientVisibleInView,
  normalizePatientTransportState,
  normalizePatientList,
  hasPatientOnBoard,
  getPatientCaregiver,
  shouldDisplayPatientCaregiver,
}
